import React, { useEffect, useRef } from 'react';
import { Bar } from '@antv/g2plot';
import type { ChartField } from '@/types/chat';
import { CHART_COLORS } from '@/common/constants';
import { toNum } from '@/utils';

interface BarChartProps {
  data: Record<string, unknown>[];
  dimensions?: ChartField[];
  metrics?: ChartField[];
}

/**
 * G2Plot Bar 为横向条形图：
 *   xField = 数值字段（水平轴）
 *   yField = 类目字段（垂直轴）
 */

function foldData(
  data: Record<string, unknown>[],
  categoryField: string,
  metricFields: ChartField[],
): Record<string, unknown>[] {
  const result: Record<string, unknown>[] = [];
  for (const row of data) {
    for (const m of metricFields) {
      result.push({
        [categoryField]: row[categoryField],
        __value: toNum(row[m.code]),
        __type: m.name ?? m.code,
      });
    }
  }
  return result;
}

/** 单指标时直接将原始数据中的数值字段转为 number */
function normalizeData(
  data: Record<string, unknown>[],
  categoryField: string,
  metricCode: string,
): Record<string, unknown>[] {
  return data.map((row) => ({ ...row, [metricCode]: toNum(row[metricCode]) }));
}

export const BarChart: React.FC<BarChartProps> = ({ data, dimensions, metrics }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<Bar | null>(null);

  const resolveFields = (d: Record<string, unknown>[]) => {
    const keys = d?.length ? Object.keys(d[0]) : [];
    const categoryField = dimensions?.[0]?.code ?? keys[0] ?? 'category';
    const metricFields: ChartField[] = metrics?.length
      ? metrics
      : keys.slice(1).map((k) => ({ code: k, name: k }));
    return { categoryField, metricFields };
  };

  const buildChartData = (d: Record<string, unknown>[]) => {
    const { categoryField, metricFields } = resolveFields(d);
    const isMultiMetric = metricFields.length > 1;
    return {
      categoryField,
      metricFields,
      chartData: isMultiMetric
        ? foldData(d, categoryField, metricFields)
        : normalizeData(d, categoryField, metricFields[0]?.code ?? 'value'),
      valueField: isMultiMetric ? '__value' : (metricFields[0]?.code ?? 'value'),
      seriesField: isMultiMetric ? '__type' : undefined,
    };
  };

  useEffect(() => {
    if (!containerRef.current || !data?.length) return;

    const { categoryField, metricFields, chartData, valueField, seriesField } = buildChartData(data);
    const categoryTitle = dimensions?.[0]?.name ?? dimensions?.[0]?.code ?? categoryField;
    const valueTitle = metricFields.map((m) => m.name ?? m.code).join(' & ');

    chartRef.current = new Bar(containerRef.current, {
      data: chartData,
      xField: valueField,   // G2Plot Bar：x = 数值（水平）
      yField: categoryField, // G2Plot Bar：y = 类目（垂直）
      ...(seriesField ? { seriesField, isGroup: true } : {}),
      autoFit: true,
      height: 260,
      color: CHART_COLORS,
      xAxis: {
        title: { text: valueTitle },
        nice: true,
        tickCount: 5,
      },
      yAxis: {
        title: { text: categoryTitle },
        label: { autoEllipsis: true },
      },
      legend: seriesField ? { position: 'top' } : false,
      tooltip: { showContent: true },
    });

    chartRef.current.render();

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!chartRef.current || !data?.length) return;
    const { chartData } = buildChartData(data);
    chartRef.current.changeData(chartData);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return <div ref={containerRef} style={{ height: '100%', width: '100%' }} />;
};
